<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">人员管理</h2>
      </div>
      <div class="page-header-extra">
        <span>在职 <b>{{ list.filter(i => i.status === 1).length }}</b> 人</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="姓名/手机">
            <el-input v-model="query.keyword" placeholder="姓名或手机号" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="所属公司">
            <el-select v-model="query.company_id" placeholder="全部" clearable style="width: 170px">
              <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="人员类型">
            <el-select v-model="query.type" placeholder="全部" clearable style="width: 120px">
              <el-option label="员工" :value="1" />
              <el-option label="外部人员" :value="2" />
            </el-select>
          </el-form-item>
          <el-form-item label="岗位">
            <el-select v-model="query.position" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="p in positions" :key="p.value" :label="p.label" :value="p.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button icon="el-icon-c-scale-to-original" size="small" @click="openQrSetting">二维码设置</el-button>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增人员</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="name" label="姓名" width="110"/>
        <el-table-column label="类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 1 ? '' : 'warning'" size="mini" effect="plain">
              {{ scope.row.type === 1 ? '员工' : '外部' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="所属公司" min-width="160">
          <template slot-scope="scope">{{ companyName(scope.row && scope.row.company_id) || '—' }}</template>
        </el-table-column>
        <el-table-column label="部门" width="130">
          <template slot-scope="scope">{{ deptName(scope.row && scope.row.dept_id) || '—' }}</template>
        </el-table-column>
        <el-table-column label="岗位" width="100">
          <template slot-scope="scope">{{ positionLabel(scope.row && scope.row.position) || '—' }}</template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="130"/>
        <el-table-column prop="entry_date" label="入职日期" width="120"/>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini" effect="plain">
              {{ scope.row.status === 1 ? '在职' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="Number(scope.row.qr_enabled) !== 0"
              type="text"
              size="mini"
              @click="handleQr(scope.row)"
            >分享二维码</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 分享二维码 -->
    <person-qr-dialog
      :visible.sync="qrVisible"
      :person="qrPerson || {}"
      :company-name="companyName(qrPerson && qrPerson.company_id)"
      :position-label="positionLabel(qrPerson && qrPerson.position)"
    />

    <!-- 二维码设置：批量开启/关闭推广二维码，也可单个切换（写 tb_sys_person.qr_enabled） -->
    <el-dialog title="二维码设置" :visible.sync="qrSettingVisible" width="820px" append-to-body>
      <div class="qr-setting-bar">
        <el-input
          v-model="qrQuery.keyword"
          placeholder="姓名或手机号"
          size="small"
          clearable
          style="width: 200px"
          @keyup.enter.native="handleQrSearch"
        />
        <el-button size="small" type="primary" icon="el-icon-search" @click="handleQrSearch">查询</el-button>
        <div class="qr-setting-actions">
          <el-button size="small" type="success" plain :disabled="!qrSelection.length" @click="batchQrEnabled(1)">批量开启</el-button>
          <el-button size="small" plain :disabled="!qrSelection.length" @click="batchQrEnabled(0)">批量关闭</el-button>
          <span class="qr-selected-tip">已选 {{ qrSelection.length }} 人</span>
        </div>
      </div>

      <el-table
        v-loading="qrLoading"
        :data="qrList"
        border
        size="small"
        height="380"
        @selection-change="onQrSelectionChange"
      >
        <el-table-column type="selection" width="46" />
        <el-table-column prop="name" label="姓名" width="120" />
        <el-table-column label="所属公司" min-width="180">
          <template slot-scope="scope">{{ companyName(scope.row.company_id) || '—' }}</template>
        </el-table-column>
        <el-table-column label="岗位" width="100">
          <template slot-scope="scope">{{ positionLabel(scope.row.position) || '—' }}</template>
        </el-table-column>
        <el-table-column label="二维码" width="120" align="center">
          <template slot-scope="scope">
            <el-switch :value="Number(scope.row.qr_enabled) === 1" @change="toggleQr(scope.row, $event)" />
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="qrPage"
          :page-size="qrPageSize"
          :total="qrTotal"
          layout="total, prev, pager, next"
          @current-change="handleQrPageChange"
        />
      </div>

      <span slot="footer">
        <el-button size="small" @click="qrSettingVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="form.id ? '编辑人员' : '新增人员'" :visible.sync="dialogVisible" width="720px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="110px">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="姓名" prop="name">
              <el-input v-model="form.name" maxlength="64" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="form.phone" maxlength="32" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="所属公司" prop="company_id">
              <el-select v-model="form.company_id" style="width: 100%" @change="handleCompanyChange">
                <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所属部门">
              <el-select v-model="form.dept_id" clearable placeholder="未分配" style="width: 100%">
                <el-option v-for="d in depts" :key="d.id" :label="d.name" :value="d.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="人员类型">
              <el-radio-group v-model="form.type">
                <el-radio :label="1">员工</el-radio>
                <el-radio :label="2">外部</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col v-if="showPosition" :span="12">
            <el-form-item label="岗位">
              <el-select v-model="form.position" clearable style="width: 100%">
                <el-option v-for="p in positions" :key="p.value" :label="p.label" :value="p.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="入职日期">
          <el-date-picker v-model="form.entry_date" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="255" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="在职" inactive-text="停用" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" @click="submit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPersonList, getPersonDetail, createPerson, updatePerson, deletePerson, getCompanyOptions, getDeptOptions, setPersonQrEnabled } from '@/api/system'
import PersonQrDialog from '@/components/PersonQrDialog.vue'

const emptyForm = () => ({
  id: 0, company_id: 1, dept_id: undefined, type: 1, name: '', phone: '',
  position: '', entry_date: '', remark: '', status: 1
})

export default {
  name: 'PersonManage',
  components: { PersonQrDialog },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { keyword: '', company_id: '', type: '', position: '' },
      page: 1,
      pageSize: 10,
      dialogVisible: false,
      // 分享二维码弹窗：二维码与推广链接由接口下发（GET /system/person/{id}/share-qr）
      qrVisible: false,
      qrPerson: {},
      // 二维码设置弹窗（批量开关 tb_sys_person.qr_enabled）
      qrSettingVisible: false,
      qrLoading: false,
      qrList: [],
      qrSelection: [],
      qrQuery: { keyword: '' },
      qrPage: 1,
      qrPageSize: 10,
      qrTotal: 0,
      form: emptyForm(),
      depts: [], // 所属部门下拉（走部门管理接口，name 为全路径）
      companies: [], // 所属公司下拉（走公司管理接口，部门下拉待对应后端建成后再换）
      positions: [
        { value: 'pickup', label: '接送' },
        { value: 'reception', label: '接待' },
        { value: 'guide', label: '讲解' },
        { value: 'operation', label: '运营' },
        { value: 'verify', label: '核销' }
      ],
      rules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        phone: [{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }],
        company_id: [{ required: true, message: '请选择所属公司', trigger: 'change' }]
      }
    }
  },
  computed: {
    // 岗位显示条件：外部人员始终可选；员工默认不显示，但已有岗位值（历史数据）时照常显示，
    // 避免编辑时把库里已有的岗位当成"不存在"给丢掉（岗位是可选字段，前后端都不强制）
    showPosition() {
      return Number(this.form.type) === 2 || !!this.form.position
    }
  },
  created() {
    this.loadData()
    // 先拉公司下拉，再按默认公司拉部门（子企业账号可选公司少，默认值可能不在选项里，需要回落）
    this.loadCompanies().then(() => this.loadDepts(this.form.company_id))
  },
  methods: {
    loadCompanies() {
      // 所属公司下拉：走公司管理 options 接口（仅正常状态）
      return getCompanyOptions().then(res => {
        this.companies = (res.data || [])
        // 默认公司不在当前账号可选范围时回落到第一个（主公司排在最前）
        if (this.companies.length && !this.companies.some(c => Number(c.id) === Number(this.form.company_id))) {
          this.form.company_id = this.companies[0].id
        }
      })
    },
    // 所属部门下拉：走部门管理 options 接口（仅启用状态，name 为全路径）
    // 部门跟着公司走，必须带 company_id，不传会列出别的公司的部门
    loadDepts(companyId) {
      const params = {}
      if (companyId !== '' && companyId !== null && companyId !== undefined) params.company_id = companyId
      getDeptOptions(params).then(res => {
        this.depts = (res.data || [])
      })
    },
    // 切换所属公司：清掉已选的部门（原部门多半不属于新公司），并重拉该公司部门
    handleCompanyChange(val) {
      this.form.dept_id = undefined
      this.loadDepts(val)
    },
    // 组装查询参数（空值不传，后端按缺失处理）
    buildParams() {
      const params = { page: this.page, page_size: this.pageSize }
      const q = this.query
      if (q.keyword && q.keyword.trim()) params.keyword = q.keyword.trim()
      if (q.company_id !== '' && q.company_id !== null) params.company_id = q.company_id
      if (q.type !== '' && q.type !== null) params.type = q.type
      if (q.position) params.position = q.position
      return params
    },
    loadData() {
      this.loading = true
      getPersonList(this.buildParams()).then(res => {
        if (res.code === 200) {
          this.list = res.data.list || []
          this.total = res.data.total || 0
        }
      }).finally(() => {
        this.loading = false
      })
    },
    // 传 undefined/null 时也要安全返回空串（历史上 render 阶段直接读 id 崩过）
    companyName(id) {
      if (id === undefined || id === null || id === '') return ''
      const list = Array.isArray(this.companies) ? this.companies : []
      const c = list.find(i => String(i.id) === String(id))
      return c ? c.name : '—'
    },
    deptName(id) {
      if (id === undefined || id === null || id === '') return ''
      const list = Array.isArray(this.depts) ? this.depts : []
      const d = list.find(i => String(i.id) === String(id))
      return d ? d.name : ''
    },
    positionLabel(val) {
      if (!val) return ''
      const list = Array.isArray(this.positions) ? this.positions : []
      const p = list.find(i => i.value === val)
      return p ? p.label : ''
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', company_id: '', type: '', position: '' }
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleAdd() {
      this.form = emptyForm()
      // 默认公司取当前账号可选的第一家（主公司排最前），再按它拉部门
      if (this.companies.length) this.form.company_id = this.companies[0].id
      this.loadDepts(this.form.company_id)
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // 编辑走详情接口回显（列表手机号脱敏，详情不脱敏）
    handleEdit(row) {
      getPersonDetail(row.id).then(res => {
        if (res.code === 200) {
          this.form = Object.assign(emptyForm(), res.data)
          // dept_id=0（未分配）不渲染成 "0"，显示 placeholder
          if (!this.form.dept_id) this.form.dept_id = undefined
          // 部门下拉按该人员所属公司拉，否则回显的部门不在选项里会显示空白
          this.loadDepts(this.form.company_id)
          this.dialogVisible = true
          this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
        }
      })
    },
    // 分享二维码：只用列表行数据（姓名/公司/岗位），二维码与推广链接由接口下发
    // GET /system/person/{id}/share-qr（接口不可用时组件内部本地兜底绘制）
    handleQr(row) {
      this.qrPerson = row || {}
      this.qrVisible = true
    },

    // ---------- 二维码设置（批量开关 tb_sys_person.qr_enabled） ----------
    openQrSetting() {
      this.qrSettingVisible = true
      this.qrQuery.keyword = ''
      this.qrPage = 1
      this.qrSelection = []
      this.loadQrList()
    },
    loadQrList() {
      this.qrLoading = true
      const params = { page: this.qrPage, page_size: this.qrPageSize }
      const keyword = (this.qrQuery.keyword || '').trim()
      if (keyword) params.keyword = keyword
      return getPersonList(params).then(res => {
        // 拦截器业务错误也 resolve，必须自判 code
        if (res.code !== 200) return
        const data = res.data || {}
        this.qrList = data.list || []
        this.qrTotal = data.total || 0
      }).finally(() => {
        this.qrLoading = false
      })
    },
    handleQrSearch() {
      this.qrPage = 1
      this.loadQrList()
    },
    handleQrPageChange(page) {
      this.qrPage = page
      this.loadQrList()
    },
    onQrSelectionChange(rows) {
      this.qrSelection = rows || []
    },
    // 单个切换：先乐观改本行，接口失败再回滚（开关是受控的 :value，改回原值即还原视觉）
    toggleQr(row, val) {
      const prev = Number(row.qr_enabled) === 1 ? 1 : 0
      const next = val ? 1 : 0
      row.qr_enabled = next
      return setPersonQrEnabled({ ids: [row.id], qr_enabled: next }).then(res => {
        if (res.code !== 200) {
          row.qr_enabled = prev
          this.$message.error((res && res.msg) ? res.msg : '设置失败')
          return
        }
        this.$message.success(next === 1 ? '已开启推广二维码' : '已关闭推广二维码')
      }).catch(() => {
        row.qr_enabled = prev
        this.$message.error('设置失败，请稍后再试')
      })
    },
    // 批量开启 / 关闭（勾选行，二次确认）
    batchQrEnabled(qrEnabled) {
      const ids = (this.qrSelection || []).map(i => i.id)
      if (!ids.length) return
      const action = qrEnabled === 1 ? '开启' : '关闭'
      this.$confirm(`确认${action}所选 ${ids.length} 人的推广二维码？`, '二维码设置', { type: 'warning' }).then(() => {
        return setPersonQrEnabled({ ids: ids, qr_enabled: qrEnabled }).then(res => {
          if (res.code !== 200) {
            this.$message.error((res && res.msg) ? res.msg : '设置失败')
            return
          }
          const updated = (res.data && res.data.updated) || 0
          this.$message.success(`已${action} ${updated} 人`)
          this.loadQrList()
        })
      }).catch(() => {})
    },
    handleDelete(row) {      this.$confirm(`确认删除人员「${row.name}」？`, '删除确认', { type: 'warning' }).then(() => {
        deletePerson(row.id).then(res => {
          if (res.code === 200) {
            this.$message.success('已删除')
            this.loadData()
          }
        })
      }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const data = {
          name: this.form.name,
          phone: this.form.phone,
          company_id: this.form.company_id,
          dept_id: this.form.dept_id || 0,
          type: this.form.type,
          position: this.form.position || '',
          entry_date: this.form.entry_date || '',
          remark: this.form.remark || '',
          status: this.form.status
        }
        const request = this.form.id
          ? updatePerson(this.form.id, data)
          : createPerson(data)
        request.then(res => {
          if (res.code === 200) {
            this.$message.success(this.form.id ? '已保存修改' : '新增成功')
            this.dialogVisible = false
            this.loadData()
          }
        })
      })
    }
  }
}
</script>

<style scoped>
/* 二维码设置弹框顶部工具条：左搜索、右批量操作（弹窗 append-to-body，scoped 属性仍在元素上，可命中） */
.qr-setting-bar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.qr-setting-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.qr-selected-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #8a7a66;
}
</style>
